import { useEffect, useState, useCallback } from "react";
import { Hash, Layers, ChevronDown, Camera, MapPin, CalendarClock, KeyRound, FileText } from "lucide-react";
import api from "../../lib/api";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const _fileUrl = (u) => (u && u.startsWith("http")) ? u : `${BACKEND}${u || ""}`;
const _isImage = (d) => {
    const m = (d?.mime || "").toLowerCase();
    if (m.startsWith("image/")) return true;
    if (m.includes("pdf")) return false;
    return /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(d?.url || d?.name || "");
};
const _docDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso || ""; } };
const INTAKE_KIND_LABELS = {
    zb1: "ZB1 / Fahrzeugschein", coc: "COC", id: "Ausweis", invoice: "Rechnung",
    damage: "Schadensfoto", standort: "Abstellort", vehicle: "Fahrzeugfoto",
};

/**
 * Dispositions-Details (Feb-2026, User-Wunsch):
 *  - Lagerort & Interner Vermerk wurden in den Standort-Block hochgezogen
 *    (Komponente DispoLocationNote, gemeinsam mit der öffentlichen Adresse).
 *  - Hier verbleibt nur die aus dem Bestellcode abgeleitete Modellreihen-Gruppierung
 *    (read-only; der Bestellcode selbst wird im VIN-Bereich gepflegt).
 *  - Jun-2026 (User-Wunsch): Eingangs-Dokumentation (beim Fahrzeug-Eingang-Scan
 *    archivierte Fotos/Belege) als Galerie mit Eingangsdatum & Standort.
 */
export default function DispoDetailsCard({ listingId, listing }) {
    const orderCode = (listing?.dispo_order_code || "").trim();
    const [series, setSeries] = useState(null);
    const [seriesOpen, setSeriesOpen] = useState(false);

    const intakeDocs = Array.isArray(listing?.dispo_intake_documents) ? listing.dispo_intake_documents : [];
    const arrivedAt = listing?.dispo_arrived_at;
    const arrivalBranch = listing?.dispo_arrival_branch_label;
    const keyTag = listing?.key_tag;

    const fetchSeries = useCallback(async () => {
        try {
            const { data } = await api.get(`/listings/${listingId}/dispo/model-series`);
            setSeries(data);
        } catch {
            setSeries(null);
        }
    }, [listingId]);

    useEffect(() => { fetchSeries(); }, [fetchSeries, listing?.dispo_order_code]);

    return (
        <div className="mt-4 rounded-sm border-2 border-gray-200 p-4" data-testid="dispo-details-card">
            {(intakeDocs.length > 0 || arrivedAt) && (
                <div className="mb-4 rounded-sm border-2 border-emerald-200 bg-emerald-50/40 p-3" data-testid="dispo-intake-doc-card">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 inline-flex items-center gap-1.5">
                        <Camera className="w-3.5 h-3.5" /> Eingangs-Dokumentation (Fahrzeug-Eingang)
                    </label>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 mb-2.5">
                        {arrivedAt && (
                            <span className="inline-flex items-center gap-1" data-testid="dispo-intake-date">
                                <CalendarClock className="w-3.5 h-3.5 text-gray-400" /> Eingang: <b className="text-[#111827]">{_docDate(arrivedAt)}</b>
                            </span>
                        )}
                        {arrivalBranch && (
                            <span className="inline-flex items-center gap-1" data-testid="dispo-intake-branch">
                                <MapPin className="w-3.5 h-3.5 text-gray-400" /> Standort: <b className="text-[#111827]">{arrivalBranch}</b>
                            </span>
                        )}
                        {keyTag && (
                            <span className="inline-flex items-center gap-1" data-testid="dispo-intake-keytag">
                                <KeyRound className="w-3.5 h-3.5 text-gray-400" /> Schlüsseldepot: <b className="text-[#111827]">{keyTag}</b>
                            </span>
                        )}
                    </div>
                    {intakeDocs.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="dispo-intake-gallery">
                            {intakeDocs.map((d, i) => {
                                const href = _fileUrl(d.url);
                                const label = INTAKE_KIND_LABELS[d.kind] || d.kind || "Beleg";
                                return _isImage(d) ? (
                                    <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                                        className="group relative block aspect-square rounded-sm overflow-hidden border border-gray-200 bg-white hover:border-[#111827]"
                                        title={`${label} · ${_docDate(d.scanned_at)}`} data-testid={`dispo-intake-photo-${i}`}>
                                        <img src={href} alt={label} className="w-full h-full object-cover" loading="lazy" />
                                        <span className="absolute bottom-0 inset-x-0 bg-black/80 text-white text-[9px] font-bold px-1 py-0.5 truncate">{label}</span>
                                    </a>
                                ) : (
                                    <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                                        className="flex flex-col items-center justify-center gap-1 aspect-square rounded-sm border border-gray-200 bg-white hover:border-[#111827] p-1 text-center"
                                        title={`${label} · ${_docDate(d.scanned_at)}`} data-testid={`dispo-intake-file-${i}`}>
                                        <FileText className="w-6 h-6 text-gray-400" />
                                        <span className="text-[9px] font-bold text-gray-600 truncate w-full">{label}</span>
                                    </a>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-[11px] text-gray-400" data-testid="dispo-intake-empty">Keine Fotos/Belege beim Eingang erfasst.</p>
                    )}
                </div>
            )}

            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" /> Hersteller-Bestellcode (Modellreihe)
            </label>
            {orderCode ? (
                <div className="text-sm font-mono-num font-bold text-[#111827]" data-testid="dispo-order-code-value">{orderCode}</div>
            ) : (
                <p className="text-[11px] text-gray-400" data-testid="dispo-order-code-empty">
                    Noch kein Bestellcode hinterlegt — im VIN-Bereich des Inserats eintragen.
                </p>
            )}

            {series && !series.applicable && (
                <p className="mt-2 text-[11px] text-gray-400" data-testid="dispo-series-na">
                    Modellreihen-Gruppierung gilt nur für „Neufahrzeug" oder „Tageszulassung" (aktuell: {series.condition || "—"}).
                </p>
            )}

            {series && series.applicable && orderCode && (
                <div className="mt-3 rounded-sm border-2 border-gray-200 overflow-hidden" data-testid="dispo-model-series">
                    <button type="button" onClick={() => setSeriesOpen((v) => !v)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                        data-testid="dispo-series-toggle">
                        <span className="inline-flex items-center gap-2 text-sm font-bold">
                            <Layers className="w-4 h-4 text-[#111827]" />
                            Modellreihe {series.condition}
                            <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#111827] text-white text-[11px] font-mono-num" data-testid="dispo-series-count">{series.count}</span>
                            <span className="text-[11px] font-normal text-gray-500">identische Fahrzeuge</span>
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${seriesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {seriesOpen && (
                        <ul className="divide-y divide-gray-100">
                            {series.items.map((it) => (
                                <li key={it.listing_id} className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${it.is_current ? "bg-amber-50" : ""}`} data-testid={`series-item-${it.listing_id}`}>
                                    <span className="inline-flex items-center gap-2 min-w-0">
                                        <span className="font-mono-num font-bold">{it.vehicle_number}</span>
                                        {it.is_current && <span className="text-[10px] font-bold uppercase text-amber-600">aktuell</span>}
                                    </span>
                                    <span className="font-mono-num text-[11px] text-gray-500 truncate">FIN {it.vin}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
